/**
 * Endpoints de **Finanzas → Transferencias** (`/api/finanzas/transferencias`).
 *
 * El listado trae, además de las filas paginadas, los totales del filtro y los
 * catálogos de la pantalla (cuentas bancarias, métodos de pago y turnos), así que
 * no hace falta pedirlos aparte. La verificación y la edición del movimiento son
 * `multipart`, porque el comprobante viaja en el mismo formulario.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '@/lib/http/api-client';

import type {
  CuerpoTransferencia,
  EstadoTransferencias,
  FiltrosTransferencias,
  ListadoTransferencias,
  MovimientoTransferenciaRespuesta,
  VentaTransferenciaRespuesta,
} from './transferencias.types';

export const transferenciasKeys = {
  todo: ['finanzas', 'transferencias'] as const,
  lista: (filtros: FiltrosTransferencias) =>
    ['finanzas', 'transferencias', 'lista', filtros] as const,
  venta: (tipo: string, ventaId: number) =>
    ['finanzas', 'transferencias', 'venta', tipo, ventaId] as const,
  movimiento: (id: number) => ['finanzas', 'transferencias', 'movimiento', id] as const,
};

/** Cadena de consulta del listado. */
export function cadenaDeTransferencias(filtros: FiltrosTransferencias): string {
  const parametros = new URLSearchParams();

  if (filtros.desde) parametros.set('desde', filtros.desde);
  if (filtros.hasta) parametros.set('hasta', filtros.hasta);
  if (filtros.estado && filtros.estado !== 'todas') parametros.set('estado', filtros.estado);
  if (filtros.turno) parametros.set('turno', String(filtros.turno));
  if (filtros.buscar) parametros.set('buscar', filtros.buscar);
  if (filtros.page > 1) parametros.set('page', String(filtros.page));
  if (filtros.perPage) parametros.set('per_page', String(filtros.perPage));

  const cadena = parametros.toString();

  return cadena ? `?${cadena}` : '';
}

/** Formulario de la verificación y de la edición (el comprobante es opcional). */
function formularioDe(datos: CuerpoTransferencia): FormData {
  const formulario = new FormData();

  if (datos.tipo) formulario.set('tipo', datos.tipo);
  if (datos.venta_id) formulario.set('venta_id', datos.venta_id);

  formulario.set('cuenta_id', datos.cuenta_id);
  formulario.set('metodo_pago_id', datos.metodo_pago_id);
  formulario.set('fecha', datos.fecha);
  formulario.set('referencia', datos.referencia);
  formulario.set('notas', datos.notas);

  if (datos.comprobante) formulario.set('voucher_file', datos.comprobante);

  return formulario;
}

export const transferenciasApi = {
  listar: (filtros: FiltrosTransferencias, signal?: AbortSignal) =>
    apiClient.get<ListadoTransferencias>(
      `/api/finanzas/transferencias${cadenaDeTransferencias(filtros)}`,
      { signal },
    ),

  venta: (tipo: string, ventaId: number, signal?: AbortSignal) =>
    apiClient.get<VentaTransferenciaRespuesta>(
      `/api/finanzas/transferencias/venta/${tipo}/${ventaId}`,
      { signal },
    ),

  verificar: (datos: CuerpoTransferencia) =>
    apiClient.post<{ movement_id: number; message: string }>(
      '/api/finanzas/transferencias/verificar',
      { body: formularioDe(datos) },
    ),

  movimiento: (id: number, signal?: AbortSignal) =>
    apiClient.get<MovimientoTransferenciaRespuesta>(
      `/api/finanzas/transferencias/movimiento/${id}`,
      { signal },
    ),

  actualizarMovimiento: (id: number, datos: CuerpoTransferencia) =>
    apiClient.put<{ id: number; message: string }>(
      `/api/finanzas/transferencias/movimiento/${id}`,
      { body: formularioDe(datos) },
    ),

  anularMovimiento: (id: number) =>
    apiClient.delete<{ id: number; message: string }>(
      `/api/finanzas/transferencias/movimiento/${id}`,
    ),

  urlComprobante: (id: number) => `/api/finanzas/transferencias/movimiento/${id}/comprobante`,
};

/** Listado paginado con sus totales y catálogos. */
export function useTransferencias(filtros: FiltrosTransferencias) {
  return useQuery({
    queryKey: transferenciasKeys.lista(filtros),
    queryFn: ({ signal }) => transferenciasApi.listar(filtros, signal),
    placeholderData: (anterior) => anterior,
  });
}

/** La venta que se va a verificar o a consultar. */
export function useVentaTransferencia(tipo: string | null, ventaId: number | null) {
  return useQuery({
    queryKey: transferenciasKeys.venta(tipo ?? '', ventaId ?? 0),
    queryFn: ({ signal }) => transferenciasApi.venta(tipo as string, ventaId as number, signal),
    enabled: Boolean(tipo) && Number(ventaId) > 0,
  });
}

/** El movimiento bancario de una transferencia verificada. */
export function useMovimientoTransferencia(id: number | null) {
  return useQuery({
    queryKey: transferenciasKeys.movimiento(id ?? 0),
    queryFn: ({ signal }) => transferenciasApi.movimiento(id as number, signal),
    enabled: Number(id) > 0,
  });
}

/** Verificar una transferencia: crea el movimiento y marca la venta. */
export function useVerificarTransferencia() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (datos: CuerpoTransferencia) => transferenciasApi.verificar(datos),
    onSuccess: () => cliente.invalidateQueries({ queryKey: transferenciasKeys.todo }),
  });
}

/** Editar el movimiento creado al verificar. */
export function useActualizarMovimientoTransferencia() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: ({ id, datos }: { id: number; datos: CuerpoTransferencia }) =>
      transferenciasApi.actualizarMovimiento(id, datos),
    onSuccess: (_resultado, variables) => {
      void cliente.invalidateQueries({ queryKey: transferenciasKeys.todo });
      void cliente.invalidateQueries({ queryKey: transferenciasKeys.movimiento(variables.id) });
    },
  });
}

/** Anular el movimiento: libera la venta para volver a verificar. */
export function useAnularMovimientoTransferencia() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => transferenciasApi.anularMovimiento(id),
    onSuccess: () => cliente.invalidateQueries({ queryKey: transferenciasKeys.todo }),
  });
}

/** Etiquetas del estado del listado, para los botones de pestaña. */
export const ESTADOS: { valor: EstadoTransferencias; etiqueta: string }[] = [
  { valor: 'todas', etiqueta: 'Todas' },
  { valor: 'pendientes', etiqueta: 'Pendientes' },
  { valor: 'verificadas', etiqueta: 'Verificadas' },
];
